// src/components/common/ChunkErrorBoundary.tsx
// Error boundary that catches ChunkLoadError and offers a reload.
// Wraps the Suspense boundary in App.tsx so failed lazy-loads show a
// user-friendly message instead of a white screen.

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ChunkErrorBoundary] Caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('Failed to fetch dynamically imported module');

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          gap={2}
          px={3}
          textAlign="center"
        >
          <WifiOffIcon sx={{ fontSize: 56, color: '#94a3b8' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>
            {isChunkError
              ? 'فشل تحميل الصفحة'
              : 'حدث خطأ غير متوقع'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 400 }}>
            {isChunkError
              ? 'يبدو أن اتصال الإنترنت غير مستقر. اضغط على الزر أدناه لإعادة المحاولة.'
              : 'حدث خطأ أثناء عرض هذه الصفحة. حاول إعادة تحميل الصفحة.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleRetry}
            sx={{
              mt: 1,
              bgcolor: '#6366f1',
              '&:hover': { bgcolor: '#4f46e5' },
              borderRadius: '12px',
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            إعادة تحميل الصفحة
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
